<script setup lang="ts">
import type { Category } from '#shared/types/deal'

const { user, logout } = useAuth()
const colorMode = useColorMode()
const router = useRouter()
const config = useRuntimeConfig()
const { trackEvent } = useUmami()

const searchQuery = ref('')
const mobileMenuOpen = ref(false)
const mobileSearchOpen = ref(false)
const telegramGroupUrl = 'https://t.me/descontito_ofertas'

const { data: categories } = useFetch<Category[]>('/categories', {
  baseURL: config.public.apiBaseUrl,
  key: 'header-categories',
  server: false,
  default: () => [],
})

function handleSearch() {
  const q = searchQuery.value.trim()
  if (q) {
    trackEvent('search-submit', { query: q, source: mobileSearchOpen.value ? 'mobile-header' : 'desktop-header' })
    router.push({ path: '/busca', query: { q } })
    mobileMenuOpen.value = false
    mobileSearchOpen.value = false
  }
}

function toggleColorMode() {
  colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark'
  trackEvent('theme-toggle', { theme: colorMode.preference })
}

async function closeAndLogout() {
  mobileMenuOpen.value = false
  await logout()
}

function goToCategory(slug?: string) {
  mobileMenuOpen.value = false
  trackEvent('category-nav', { category: slug ?? 'all', source: 'mobile-menu' })
  navigateTo(slug ? `/categoria/${slug}` : '/')
}

function toggleMobileSearch() {
  mobileSearchOpen.value = !mobileSearchOpen.value
  trackEvent('mobile-search-toggle', { open: mobileSearchOpen.value })
}

function trackTelegramClick(source: 'desktop-header' | 'mobile-menu') {
  trackEvent('telegram-group-click', { source })
}

const accountLinks = computed(() => {
  if (user.value) {
    const items = [
      { label: 'Minha Conta', to: '/minha-conta', icon: 'i-heroicons-user' },
      { label: 'Favoritos', to: '/minha-conta/favoritos', icon: 'i-heroicons-heart' },
    ]

    if (user.value.role === 'ADMIN' || user.value.role === 'MODERATOR') {
      items.push({ label: 'Admin', to: '/admin', icon: 'i-heroicons-shield-check' })
    }

    return items
  }

  return [
    { label: 'Entrar', to: '/login', icon: 'i-heroicons-arrow-right-on-rectangle' },
    { label: 'Cadastrar', to: '/registro', icon: 'i-heroicons-user-plus' },
  ]
})
</script>

<template>
  <header class="sticky top-0 z-50 border-b border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur">
    <div class="container mx-auto px-4 py-3 md:py-3">
      <div v-if="mobileSearchOpen" class="flex items-center gap-2 lg:hidden">
        <div class="min-w-0 flex-1">
          <UInput
            v-model="searchQuery"
            icon="i-heroicons-magnifying-glass"
            placeholder="Buscar ofertas, cupons, lojas..."
            size="lg"
            autofocus
            @keyup.enter="handleSearch"
          >
            <template #trailing>
              <UButton
                icon="i-heroicons-arrow-right"
                color="primary"
                variant="ghost"
                size="xs"
                @click="handleSearch"
              />
            </template>
          </UInput>
        </div>
        <UButton
          color="neutral"
          variant="ghost"
          size="md"
          icon="i-heroicons-x-mark"
          class="min-h-10 min-w-10 rounded-full"
          aria-label="Fechar busca"
          @click="mobileSearchOpen = false"
        />
      </div>

      <div v-else class="relative flex items-center gap-3 md:gap-3 lg:gap-6">
        <UButton
          color="neutral"
          variant="ghost"
          size="md"
          icon="i-heroicons-bars-3"
          class="lg:hidden min-h-10 min-w-10 rounded-full"
          aria-label="Abrir menu"
          @click="mobileMenuOpen = true"
        />

        <NuxtLink to="/" class="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 min-w-0 shrink lg:static lg:translate-x-0 lg:shrink-0">
          <span class="text-[1.55rem] md:text-2xl lg:text-[2rem] font-black text-primary-500 leading-none tracking-tight">Descontito</span>
          <UBadge color="primary" variant="soft" size="xs" class="hidden lg:inline-flex">BR</UBadge>
        </NuxtLink>

        <div class="hidden lg:block lg:w-full lg:max-w-xl">
          <UInput
            v-model="searchQuery"
            icon="i-heroicons-magnifying-glass"
            placeholder="Buscar ofertas, cupons, lojas..."
            size="lg"
            class="w-full"
            :ui="{ base: 'h-10' }"
            @keyup.enter="handleSearch"
          >
            <template #trailing>
              <UButton
                v-if="searchQuery"
                icon="i-heroicons-arrow-right"
                color="primary"
                variant="ghost"
                size="xs"
                @click="handleSearch"
              />
            </template>
          </UInput>
        </div>

        <UButton
          color="neutral"
          variant="outline"
          size="md"
          icon="i-heroicons-magnifying-glass"
          class="ml-auto lg:hidden inline-flex items-center justify-center min-h-10 min-w-10 rounded-full border-gray-200 bg-white text-primary-500"
          aria-label="Buscar"
          @click="toggleMobileSearch"
        />

        <div class="hidden lg:ml-auto lg:flex items-center self-center gap-1">
          <UButton to="/cupons" color="neutral" variant="ghost" size="sm" class="h-10" icon="i-heroicons-tag">
            Cupons
          </UButton>
          <UButton to="/" color="neutral" variant="ghost" size="sm" class="h-10" icon="i-heroicons-fire">
            Ofertas
          </UButton>
          <UButton to="/produtos" color="neutral" variant="ghost" size="sm" class="h-10" icon="i-heroicons-chart-bar">
            Monitor de Preços
          </UButton>
          <UButton
            :href="telegramGroupUrl"
            target="_blank"
            rel="noopener noreferrer"
            color="neutral"
            variant="ghost"
            size="sm"
            class="h-10"
            icon="i-heroicons-paper-airplane"
            @click="trackTelegramClick('desktop-header')"
          >
            Telegram
          </UButton>
          <UButton
            :icon="colorMode.value === 'dark' ? 'i-heroicons-sun' : 'i-heroicons-moon'"
            color="neutral"
            variant="ghost"
            size="sm"
            class="h-10 w-10"
            @click="toggleColorMode"
          />

          <template v-if="user">
            <UButton to="/minha-conta" color="neutral" variant="ghost" size="sm" class="h-10">
              Minha Conta
            </UButton>
            <UButton
              v-if="user.role === 'ADMIN' || user.role === 'MODERATOR'"
              to="/admin"
              color="primary"
              variant="soft"
              size="sm"
              class="h-10"
            >
              Admin
            </UButton>
          </template>
          <template v-else>
            <UButton to="/login" color="neutral" variant="ghost" size="sm" class="h-10">
              Entrar
            </UButton>
            <UButton to="/registro" color="primary" size="sm" class="h-10">
              Cadastrar
            </UButton>
          </template>
        </div>
      </div>
    </div>

    <UModal v-model:open="mobileMenuOpen">
      <template #content>
        <div class="max-h-[85vh] overflow-y-auto bg-white dark:bg-gray-950 p-4 sm:p-5">
          <div class="flex items-center justify-between gap-3 mb-4">
            <div>
              <p class="text-lg font-bold text-gray-900 dark:text-white">Menu</p>
              <p class="text-sm text-gray-500 dark:text-gray-400">Navegue por ofertas e categorias.</p>
            </div>
            <UButton
              color="neutral"
              variant="ghost"
              icon="i-heroicons-x-mark"
              @click="mobileMenuOpen = false"
            />
          </div>

          <div class="space-y-2">
            <div class="rounded-2xl border border-primary-200 bg-primary-50/70 p-3 dark:border-primary-900/60 dark:bg-primary-950/30">
              <p class="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary-600 dark:text-primary-400">Comunidade</p>
              <UButton
                :href="telegramGroupUrl"
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                variant="soft"
                class="w-full justify-start"
                icon="i-heroicons-paper-airplane"
                @click="trackTelegramClick('mobile-menu')"
              >
                Grupo de ofertas no Telegram
              </UButton>
            </div>

            <div class="rounded-2xl border border-gray-200 dark:border-gray-800 p-3">
              <div class="flex items-center justify-between gap-3 mb-2">
                <p class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Conta</p>
                <UButton
                  :icon="colorMode.value === 'dark' ? 'i-heroicons-sun' : 'i-heroicons-moon'"
                  color="neutral"
                  variant="ghost"
                  size="sm"
                  @click="toggleColorMode"
                />
              </div>

              <div class="space-y-2">
                <template v-for="item in accountLinks" :key="item.label">
                  <UButton
                    :to="item.to"
                    color="neutral"
                    variant="ghost"
                    class="w-full justify-start"
                    :icon="item.icon"
                    @click="mobileMenuOpen = false"
                  >
                    {{ item.label }}
                  </UButton>
                </template>

                <UButton
                  v-if="user"
                  color="error"
                  variant="ghost"
                  class="w-full justify-start"
                  icon="i-heroicons-arrow-right-on-rectangle"
                  @click="closeAndLogout"
                >
                  Sair
                </UButton>
              </div>
            </div>

            <div class="rounded-2xl border border-gray-200 dark:border-gray-800 p-3">
              <p class="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Categorias</p>
              <div class="space-y-1">
                <UButton
                  color="primary"
                  variant="soft"
                  class="w-full justify-start"
                  icon="i-heroicons-fire"
                  @click="goToCategory()"
                >
                  Todas as Promoções
                </UButton>
                <UButton
                  v-for="category in categories"
                  :key="category.id"
                  color="neutral"
                  variant="ghost"
                  class="w-full justify-start"
                  @click="goToCategory(category.slug)"
                >
                  <template #leading>
                    <span v-if="category.icon">{{ category.icon }}</span>
                    <UIcon v-else name="i-heroicons-tag" />
                  </template>
                  {{ category.name }}
                </UButton>
              </div>
            </div>
          </div>
        </div>
      </template>
    </UModal>
  </header>
</template>
